/// <amd-module name="Build/Extensions/AzureRepos/RepositorySelection" />
import * as React from "react";

import * as Build from "Build/Client/Contracts/Build";
import { AzureReposRepositoryInfo, AzureReposRepositoryListData } from "Build/Client/Contracts/DataProvider";
import { FilterItem, Props, RepositorySelector } from "Build/Designer/RepositorySelector";
import * as Resources from "Build/Extensions/AzureRepos/Resources";

import { ProjectVisibility } from "Tfs/Platform/Contracts/Core";
import { getTFSPageData } from "Tfs/Platform/Page";

import { IVCNavigationService } from "VC/Common/Navigation";

import * as StringUtil from "VSS/Core/Util/String";

import { IVssContributionService } from "VSS/Platform/Contribution";
import { Components, IVssComponentContext } from "VSS/Platform/Layout";

export class RepositorySelection extends RepositorySelector<Props> {

    constructor(props: Props, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            ...this.state,
            resultSet: this.state.projectId
        };
    }

    protected getClassName(baseClassName: string): string {
        return baseClassName + " azure-repos";
    }

    protected getFilterItems(): FilterItem[] {
        const filterItems: FilterItem[] = [{ key: this.state.projectId, displayName: this.state.projectName }];

        // Don't allow setting up a Pipeline from a public project to other project repositories.
        // That is consistent with the behavior of the current task-based Pipeline creation UI.
        const tfsPageData = getTFSPageData(this.context.pageContext);
        if (tfsPageData && tfsPageData.project
            && tfsPageData.project.visibility !== ProjectVisibility.Public) {
            filterItems.push({ key: "all", displayName: Resources.AllProjects });
        }

        return filterItems;
    }

    protected getRepositories(
        serviceEndpointId?: string,
        filter?: string,
        resultSet?: string,
        pageResults?: boolean,
        continuationToken?: string): Promise<Build.SourceRepositories> {

        // resultSet is used as the project filter for fetching repositories.  It may be a project GUID or "all".
        // We currently do not support paging and continuation tokens with Azure Repos.
        const requestParams = { filter: resultSet };
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        return contributionService.getDataAsync<AzureReposRepositoryListData>(
            "ms.vss-build-web.azure-repos-repositories-data-provider",
            requestParams).then((repositoryData) =>
                contributionService.getContributionAsync("ms.vss-code-web.common-content").then(() => {
                    // "ms.vss-code-web.common-content" required for "IVCNavigationService"
                    const navigationService = this.context.pageContext.getService<IVCNavigationService>("IVCNavigationService");
                    const repoInfos = (repositoryData && repositoryData.repositories || []);
                    const firstProjectId = repoInfos[0] && repoInfos[0].projectId;
                    const fromMultipleProjects = !!repoInfos.find(repoInfo => repoInfo.projectId !== firstProjectId)
                    return {
                        continuationToken: "",
                        pageLength: repoInfos.length,
                        totalPageCount: 1,
                        repositories: repoInfos.map(repoInfo => this.repoInfoToSourceRepo(repoInfo, fromMultipleProjects, navigationService)),
                    }
                }));
    }

    protected getRepositoryIconClass(): string | undefined {
        return "gitRepoIcon";
    }

    protected compareRepositories(a: Build.SourceRepository, b: Build.SourceRepository): number {
        const aIsFavorite = a.properties.isFavorite.toLowerCase() === "true";
        const bIsFavorite = b.properties.isFavorite.toLowerCase() === "true";

        // Sort favorites to the top
        if (aIsFavorite && !bIsFavorite) {
            return -1;
        }
        if (!aIsFavorite && bIsFavorite) {
            return 1;
        }

        return StringUtil.localeIgnoreCaseComparer((a.fullName || a.name), (b.fullName || b.name));
    }

    private repoInfoToSourceRepo(repoInfo: AzureReposRepositoryInfo, prefixProjectName: boolean, navigationService: IVCNavigationService): Build.SourceRepository {
        const properties: { [key: string]: string } = {
            isFork: String(repoInfo.isFork),
            isFavorite: String(repoInfo.isFavorite)
        };
        const sourceRepository = {
            id: repoInfo.id,
            name: repoInfo.name,
            fullName: (prefixProjectName ? repoInfo.projectName + "/" : "") + repoInfo.name,
            defaultBranch: repoInfo.defaultBranch,
            properties,
            url: navigationService.getRepositoryUrl(repoInfo.name, repoInfo.projectName)
        } as Build.SourceRepository;

        if (repoInfo.projectId !== this.state.projectId) {
            sourceRepository.properties.repositoryProjectId = repoInfo.projectId;
        }

        return sourceRepository;
    }
}

Components.add("azure-repos-repository-selection", RepositorySelection);
